import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import {
  miniApp,
  themeParams,
  viewport,
  backButton,
} from '@tma.js/sdk-react'
import { init } from '@tma.js/sdk'
import { UserPreferencesProvider } from './contexts/UserPreferencesContext'
import AuthGuard from './components/AuthGuard'
import { BottomNavigation } from './components/BottomNavigation'
import { Layout } from './components/Layout'
import Dashboard from './components/dashboard/Dashboard'
import TraineesList from './components/user/TraineesList'
import InviteTrainee from './components/trainee/InviteTrainee'
import BecomeSpecialistFlow from './components/BecomeSpecialistFlow'
import CreateVirtualTrainee from './components/trainee/CreateVirtualTrainee'
import TraineeDetail from './components/user/TraineeDetail'
import CreateTrainingSet from './components/training/CreateTrainingSet'
import TrainingSetDetail from './components/training/TrainingSetDetail'
import EditTraining from './components/EditTraining'
import Reports from './components/Reports'
import Preferences from './components/preferences/Preferences'
import NotificationsSettings from './components/preferences/NotificationsSettings'
import MyProfilePage from './components/preferences/MyProfilePage'
import QuickAccessSettings from './components/preferences/QuickAccessSettings'
import TrainingTemplates from './components/training/TrainingTemplates'
import CreateEditTemplate from './components/training/CreateEditTemplate'
import MyCoaches from './components/user/MyCoaches'
import CoachDetail from './components/user/CoachDetail'
import Contacts from './components/user/Contacts'
import TrainingsView from './components/training/TrainingsView'
import Subscription from './components/subscription/Subscription'
import AllTrainingSets from './components/training/AllTrainingSets'
import ActiveTrainingSet from './components/training/ActiveTrainingSet'
import TrainingBlocks from './components/training/TrainingBlocks'
import CreateTrainingBlock from './components/training/CreateTrainingBlock'
import CreateTrainingBlockFromText from './components/training/CreateTrainingBlockFromText'
import EditTrainingBlock from './components/training/EditTrainingBlock'
import TrainingPrograms from './components/training/TrainingPrograms'
import CreateTrainingProgramFromText from './components/training/CreateTrainingProgramFromText'
import TrainingProgramDetail from './components/training/TrainingProgramDetail'
import CreateTrainingProgram from './components/training/CreateTrainingProgram'
import HistoryPage, { ProgramHistoryRedirect } from './components/training/HistoryPage'
import TrainingSchedulePage from './components/preferences/TrainingSchedulePage'
import SupportReportPage from './components/support/SupportReportPage'
import More from './components/more/More'
import WorkoutStart from './components/training/WorkoutStart'
import WorkoutSessionPage from './components/training/WorkoutSessionPage'
import ProViewLive from './components/training/ProViewLive'
import { TourProvider } from './onboarding/TourProvider'
import { PostHogPageview } from './components/PostHogPageview'

// Component to handle back button visibility based on routes
function BackButtonHandler() {
  const location = useLocation()
  const navigate = useNavigate()

  // Set up click handler once when component mounts
  useEffect(() => {
    const handleBackClick = () => {
      // Use navigate(-1) to go back in history
      navigate(-1)
    }

    // Set up click handler
    if (backButton.onClick.isAvailable()) {
      backButton.onClick(handleBackClick)
    } else {
      // Retry if not available yet
      const retryTimer = setTimeout(() => {
        if (backButton.onClick.isAvailable()) {
          backButton.onClick(handleBackClick)
        }
      }, 200)

      return () => clearTimeout(retryTimer)
    }

    // Cleanup: remove handler when component unmounts
    return () => {
      if (backButton.offClick.isAvailable()) {
        backButton.offClick(handleBackClick)
      }
    }
  }, [navigate])

  // Handle show/hide based on route changes
  useEffect(() => {
    // Define routes where back button should be hidden (root/home routes)
    const hideBackButtonRoutes = ['/', '/more']
    const shouldHide = hideBackButtonRoutes.includes(location.pathname)

    // Small delay to ensure SDK is ready
    const timer = setTimeout(() => {
      try {
        // Show or hide back button based on route
        if (shouldHide) {
          if (backButton.hide.isAvailable()) {
            backButton.hide()
          }
        } else {
          // Ensure it's mounted first (in case it wasn't mounted in App)
          if (backButton.mount.isAvailable()) {
            backButton.mount()
          }
          // Then show it
          if (backButton.show.isAvailable()) {
            backButton.show()
          }
        }
      } catch (error) {
        console.error('[BackButton] Error showing/hiding:', error)
      }
    }, 100)

    return () => {
      clearTimeout(timer)
    }
  }, [location.pathname])

  return null
}

function App() {
  useEffect(() => {
    // Initialize the core SDK first
    try {
      init()
    } catch {
      // SDK might already be initialized, ignore error
    }

    // Initialize mini app
    miniApp.ready()

    // Expand viewport to full height if available
    if (viewport.expand.isAvailable()) {
      viewport.expand()
    }

    // Mount components if available - ensure themeParams and miniApp are mounted early
    if (themeParams.mount.isAvailable()) {
      themeParams.mount()
    }

    if (miniApp.mount.isAvailable()) {
      miniApp.mount()
    }

    if (viewport.mount.isAvailable()) {
      viewport.mount()
    }

    // Wait for SDK to be ready, then mount back button
    const checkAndMount = () => {
      if (backButton.mount.isAvailable()) {
        backButton.mount()
      } else {
        // Retry after a short delay
        setTimeout(checkAndMount, 100)
      }
    }

    // Start checking after a short delay
    setTimeout(checkAndMount, 100)
  }, [])

  return (
    <BrowserRouter>
      <PostHogPageview />
      <UserPreferencesProvider>
        <AuthGuard>
          <TourProvider>
            <BackButtonHandler />
            <Layout>
              <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/more" element={<More />} />
              <Route path="/trainees" element={<TraineesList />} />
              <Route path="/contacts" element={<Contacts />} />
              <Route path="/become-specialist" element={<BecomeSpecialistFlow />} />
              <Route path="/invite" element={<InviteTrainee />} />
              <Route path="/create-virtual-trainee" element={<CreateVirtualTrainee />} />
              <Route path="/trainee/:id" element={<TraineeDetail />} />
              <Route path="/trainee/:id/history" element={<HistoryPage />} />
              <Route path="/trainee/:id/create-training-set" element={<CreateTrainingSet />} />
              <Route path="/trainee/:traineeId/set/:setId" element={<TrainingSetDetail />} />
              <Route path="/trainee/:traineeId/set/:setId/training/:projectionId" element={<EditTraining />} />
              <Route path="/my-coaches" element={<MyCoaches />} />
              <Route path="/coach/:id" element={<CoachDetail />} />
              <Route path="/coach/:traineeId/set/:setId" element={<TrainingSetDetail />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/preferences" element={<Preferences />} />
              <Route path="/support" element={<SupportReportPage />} />
              <Route path="/my-profile" element={<MyProfilePage />} />
              <Route path="/quick-access" element={<QuickAccessSettings />} />
              <Route path="/notifications-settings" element={<NotificationsSettings />} />
              <Route path="/subscription" element={<Subscription />} />
              <Route path="/templates" element={<TrainingTemplates />} />
              <Route path="/templates/create" element={<CreateEditTemplate />} />
              <Route path="/templates/edit/:id" element={<CreateEditTemplate />} />
              <Route path="/this-week-trainings" element={<TrainingsView />} />
              <Route path="/pro-view" element={<ProViewLive />} />
              <Route path="/pro-view-live" element={<Navigate to="/pro-view" replace />} />
              <Route path="/all-training-sets" element={<AllTrainingSets />} />
              <Route path="/active-training-set" element={<ActiveTrainingSet />} />
              <Route path="/training-blocks" element={<TrainingBlocks />} />
              <Route path="/training-blocks/create" element={<CreateTrainingBlock />} />
              <Route path="/training-blocks/create-from-text" element={<CreateTrainingBlockFromText />} />
              <Route path="/training-blocks/:id/edit" element={<EditTrainingBlock />} />
              <Route path="/training-programs" element={<TrainingPrograms />} />
              <Route path="/training-programs/create" element={<CreateTrainingProgram />} />
              <Route path="/training-programs/create-from-text" element={<CreateTrainingProgramFromText />} />
              <Route path="/history" element={<HistoryPage />} />
              <Route path="/training-programs/history" element={<ProgramHistoryRedirect />} />
              <Route path="/training-programs/:id" element={<TrainingProgramDetail />} />
              <Route path="/training-programs/:id/edit" element={<CreateTrainingProgram />} />
              <Route path="/training-schedule" element={<TrainingSchedulePage />} />
              <Route path="/training/start" element={<WorkoutStart />} />
              <Route path="/training/session/:sessionId" element={<WorkoutSessionPage />} />
              </Routes>
            </Layout>
            <BottomNavigation />
          </TourProvider>
        </AuthGuard>
      </UserPreferencesProvider>
    </BrowserRouter>
  )
}

export default App
